import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Minus, Plus, ShoppingBag } from "lucide-react";
import { motion } from "framer-motion";
import { useCart } from "@/context/CartContext";
import { api, eur } from "@/lib/api";
import { Kicker, FadeUp } from "@/components/Reveal";

export default function ProductPage() {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [qty, setQty] = useState(1);
  const [notFound, setNotFound] = useState(false);
  const { add } = useCart();

  useEffect(() => {
    api.get(`/products/${id}`)
      .then((r) => { setProduct(r.data); document.title = `${r.data.name} — Luca Mannu Giardini`; })
      .catch(() => setNotFound(true));
  }, [id]);

  if (notFound)
    return (
      <main className="mx-auto max-w-3xl px-6 pb-32 pt-48 text-center" data-testid="product-not-found">
        <h1 className="font-serif text-4xl text-forest">Prodotto non trovato</h1>
        <Link to="/negozio" className="link-underline mt-6 inline-block font-accent text-xs uppercase tracking-[0.2em] text-terra">Torna al negozio</Link>
      </main>
    );

  if (!product)
    return <main className="mx-auto max-w-3xl px-6 pb-32 pt-48 text-sage" data-testid="product-loading">Caricamento…</main>;

  return (
    <main className="mx-auto max-w-7xl px-6 pb-32 pt-36 lg:px-10" data-testid="product-page">
      <FadeUp>
        <Link to="/negozio" className="flex w-fit items-center gap-2 font-accent text-xs uppercase tracking-[0.2em] text-sage hover:text-forest" data-testid="product-back-link">
          <ArrowLeft className="h-4 w-4" strokeWidth={1.5} /> Negozio
        </Link>
      </FadeUp>
      <div className="mt-10 grid gap-14 lg:grid-cols-2">
        <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}>
          <div className="arch-frame overflow-hidden bg-sand">
            <img src={product.image} alt={product.name} className="aspect-[4/5] w-full object-cover" data-testid="product-image" />
          </div>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
          className="flex flex-col justify-center"
        >
          <Kicker>{product.category}</Kicker>
          <h1 className="mt-4 font-serif text-4xl tracking-tight text-forest md:text-5xl" data-testid="product-name">{product.name}</h1>
          <p className="mt-4 font-serif text-3xl text-moss" data-testid="product-price">{eur(product.price)}</p>
          <p className="mt-8 max-w-md leading-relaxed text-sage" data-testid="product-description">{product.description}</p>

          <div className="mt-10 flex items-center gap-6">
            <div className="flex items-center gap-4 rounded-full border border-line px-4 py-2.5">
              <button onClick={() => setQty((q) => Math.max(1, q - 1))} className="p-1 text-forest" data-testid="qty-minus" aria-label="Diminuisci">
                <Minus className="h-4 w-4" />
              </button>
              <span className="min-w-8 text-center font-accent text-lg" data-testid="qty-value">{qty}</span>
              <button onClick={() => setQty((q) => Math.min(50, q + 1))} className="p-1 text-forest" data-testid="qty-plus" aria-label="Aumenta">
                <Plus className="h-4 w-4" />
              </button>
            </div>
            <button
              onClick={() => add(product, qty)}
              data-testid="product-add-to-cart"
              className="flex flex-1 items-center justify-center gap-3 rounded-full bg-forest py-4 font-accent text-sm uppercase tracking-[0.2em] text-bone transition-transform duration-300 hover:scale-[1.02] active:scale-95"
            >
              <ShoppingBag className="h-4 w-4" strokeWidth={1.5} /> Aggiungi al carrello
            </button>
          </div>

          <div className="mt-10 border-t border-line pt-8 text-sm text-sage">
            <p>· Consegna in tutta la Sardegna, concordata dopo l'ordine</p>
            <p className="mt-2">· IVA calcolata al pagamento</p>
            <p className="mt-2">· Consigli di cura inclusi in ogni acquisto</p>
          </div>
        </motion.div>
      </div>
    </main>
  );
}
